/**
 * useAuthBranding — single source of tenant branding for the auth shell.
 *
 * Wraps `useBrandingQuery({ tenantSlug })` and:
 *   - resolves the logo to show (tenant logo when uploaded, else the bundled
 *     neutral "Link HR" mark — reusing the sentinel-swap so the backend's
 *     host-relative `/brand/...` fallback resolves on the FE host in prod);
 *   - applies the tenant primary as the global accent via `setTenantPrimary`,
 *     with a WCAG-safe fallback: an invalid color or one that fails the
 *     contrast gate (verdict `BLOCK`) falls back to the neutral Link HR teal
 *     default rather than rendering an illegible CTA;
 *   - RESETS to the default on tenant change / unmount, so one tenant's color
 *     never leaks into the next login the SPA renders.
 *
 * `passive: true` skips the side effect — used by Link Admin / platform login,
 * which has no tenant and always renders the neutral mark.
 */
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { brandingName, useBrandingQuery } from "@linkhr/api-client";
import { DEFAULT_PRIMARY_HEX, setTenantPrimary } from "@linkhr/design-system/runtime";

/**
 * Neutral logo fallback. The SVG ships in each app's `public/brand/` so it is a
 * same-origin static asset. Vite substitutes the per-app `BASE_URL`
 * (`/admin/`, `/me/`, `/`) at build time, so one literal works in all builds.
 */
// Vite substitutes the literal `import.meta.env.BASE_URL` chain per-app at
// build time. The cast keeps TypeScript happy where vite/client ambient types
// aren't loaded; aliasing `import.meta` would defeat the substitution, so we
// read the resolved value inline.
const BASE_URL = (import.meta as { env?: { BASE_URL?: string } }).env?.BASE_URL ?? "/";
const NEUTRAL_LOGO_SRC = `${BASE_URL}brand/linkhr-logo.svg`;

export interface AuthBranding {
  /** Tenant display name; "" while loading or for the neutral/platform shell. */
  tenantName: string;
  /** Resolved logo src — tenant logo when uploaded, else the neutral mark. */
  logoSrc: string;
  /** True only when the tenant has uploaded its own logo. */
  hasTenantLogo: boolean;
  isLoading: boolean;
}

export interface UseAuthBrandingOpts {
  /** Skip the tenant-primary side effect + branding fetch (platform login). */
  passive?: boolean;
}

export function useAuthBranding(tenantSlug: string, opts: UseAuthBrandingOpts = {}): AuthBranding {
  const passive = opts.passive === true;
  const branding = useBrandingQuery({ tenantSlug, enabled: !passive && tenantSlug.length > 0 });
  const { i18n } = useTranslation();

  const tenantName =
    passive || branding.data === undefined ? "" : brandingName(branding.data, i18n.language);
  // The wide logo suits the form header best; the square one otherwise.
  const remoteLogo = passive
    ? null
    : (branding.data?.wideLogoUrl ?? branding.data?.logoUrl ?? null);
  const hasTenantLogo = remoteLogo !== null;
  const logoSrc = hasTenantLogo ? remoteLogo : NEUTRAL_LOGO_SRC;
  const primaryHex = passive ? undefined : branding.data?.primaryColorHex;

  useEffect(() => {
    if (passive) return;
    // Loading, or a tenant with no colour of its own (null) → the default teal.
    const target = primaryHex || DEFAULT_PRIMARY_HEX;
    const result = setTenantPrimary(target);
    // BLOCK = invalid hex OR fails contrast vs white → never ship an illegible
    // CTA; fall back to the neutral Link HR teal default.
    if (result.verdict === "BLOCK") setTenantPrimary(DEFAULT_PRIMARY_HEX);
    // No cleanup-to-default on unmount: the effect re-applies whenever the
    // tenant (primaryHex) changes, so there is no cross-login leak, and a blind
    // reset here would clobber the authenticated shell's own tenant primary
    // during the overlapping login→app route transition.
  }, [primaryHex, passive]);

  return { tenantName, logoSrc, hasTenantLogo, isLoading: passive ? false : branding.isLoading };
}
